import {closeDialog, openDialog} from 'platform/components';

import {useSelector} from 'react-redux';

import {selectActiveBranchId, selectUserBranchOptions} from '@dms/api/features';
import {BranchPreselectionRecordType} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {Nullish} from 'shared/web';

import {CreationBranchDialog} from '../components/CreationBranchDialog/CreationBranchDialog';
import {getCreationBranchChoice} from '../utils/getCreationBranchChoice';

const CREATION_BRANCH_DIALOG_ID = 'creation-branch-dialog';

interface UseCreationBranchConfirmationProps {
  vehicleBranchId: string | Nullish;
}

/**
 * Lets the user consciously pick the branch of a Business Case or an Interest created from the
 * vehicle card when the vehicle belongs to a different branch than the selected one.
 * `onConfirm` receives the confirmed branch, or no branch when no question was needed and the
 * default (selected) branch applies. It is never called when the user cancels or closes the dialog.
 */
export function useCreationBranchConfirmation(props: UseCreationBranchConfirmationProps) {
  const activeBranchId = useSelector(selectActiveBranchId);
  const userBranchOptions = useSelector(selectUserBranchOptions) ?? [];

  const confirmCreationBranch = (
    recordType: BranchPreselectionRecordType,
    onConfirm: (branchId?: string) => void
  ) => {
    const choice = getCreationBranchChoice({
      activeBranchId,
      vehicleBranchId: props.vehicleBranchId,
      accessibleBranchIds: userBranchOptions.map((option) => option.value),
    });

    if (choice.type === 'NO_DIALOG') {
      onConfirm();
      return;
    }

    openDialog(
      <CreationBranchDialog
        recordType={recordType}
        isVehicleBranchInaccessible={choice.type === 'VEHICLE_BRANCH_INACCESSIBLE'}
        selectedBranchId={choice.activeBranchId}
        vehicleBranchId={choice.vehicleBranchId}
        onConfirm={(branchId) => {
          closeDialog(CREATION_BRANCH_DIALOG_ID);
          onConfirm(branchId);
        }}
        onCancel={() => closeDialog(CREATION_BRANCH_DIALOG_ID)}
        data-testid={testIds.vehicles.detail('creationBranchDialog')}
      />,
      {
        id: CREATION_BRANCH_DIALOG_ID,
        title:
          recordType === 'business-case'
            ? i18n.t('entity.branch.creationBranch.businessCaseTitle')
            : i18n.t('entity.branch.creationBranch.interestTitle'),
        size: 'small',
        'data-testid': testIds.vehicles.detail('creationBranchDialog'),
      }
    );
  };

  return confirmCreationBranch;
}
